import { Injectable, inject } from '@angular/core';
import { Observable, from, firstValueFrom } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { PosDataService } from '../pos.data.service';
import { CreateFinancialTransactionRequest, DenominationItem, FinancialTransactionType, TenderMovementReasonCode, TransactionTypeCodes } from '../model/create-financial-transaction.model';
import { CreateFinancialTransactionResponse as ApiTransactionResponse, CreateFinancialTransactionResponse } from '../model/create-financial-transaction-response.model';
import { TenderType } from '../model/tender-type.enum';

/**
 * Model for denomination details in a closing transaction
 */
export interface DenominationDetail {
  LineNumber: number;
  DenominationValue: number;
  Count: number; // Always a number, never an array
  RegisteredCount?: number; // Always a number, never an array
  Total: number;
  Type?: string; // 'Banknote' or 'Coin' for money closing
}

/**
 * Model for money closing transaction
 */
export interface MoneyClosingData extends ValuablesClosingData {
  
}

/**
 * Model for valuables closing transaction
 */
export interface ValuablesClosingData {
  BusinessUnitID: string | number;
  BusinessUnitName: string;
  DisplayName: string;
  CurrencyCode: string;
  InventoryDate: Date;
  Note: string;

  // PaymentTypeName for valuables closing
  ValuableType: string;
  TenderType: TenderType;
  TenderSubType?: string;

  TotalValue: number;
  TotalRegisteredValue: number;
  Difference: number;
  Surplus: number;
  Shortage: number;
  DenominationDetails: DenominationDetail[];
  PrintReceipt: boolean;
}

/**
 * Response from API
 */
export interface ClosingTransactionResponse {
  success: boolean;
  message?: string;
  transactionId?: string;
  errors?: string[];
  trxUID?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ClosingTransactionService {
  private posDataService = inject(PosDataService);
  private translateService = inject(TranslateService);

  /**
   * Submit money closing transaction to API
   */
  submitMoneyClosing(data: MoneyClosingData): Observable<ClosingTransactionResponse> {

    const request = this.buildClosingRequest(
      data, 
      TenderType.Cash,
      '', // No specific tender subtype for money closing
      TransactionTypeCodes.CashInventoryCheck, 
      FinancialTransactionType.SafeInitialValue, 
      TenderMovementReasonCode.CASH_INVENTORY_CHECK, 
      'webPos.Closings.CashInventoryCheckLabel'
    );
    return from(this.posDataService.createFinancialTransaction(request)).pipe(
      map((response: CreateFinancialTransactionResponse | null) => {
        const success = !!response;
        let trxUID: string | undefined;
        
        if (response && response.records && response.records.length > 0 && response.records[0].components) {
           const headerComponent = response.records[0].components.find((component): component is { trxUID: string } => {
             return typeof component === "object" && component !== null && "trxUID" in component;
           });
           if (headerComponent) {
             trxUID = headerComponent.trxUID;
           }
        }

        return {
            success,
            message: success ? 'webPos.Closings.MoneyClosingSubmittedMessage' : 'webPos.Closings.MoneyClosingFailedMessage',
            transactionId: success ? 'generated-id' : undefined,
            trxUID: trxUID
        };
      }),
      catchError((error) => {
        console.error('Money closing submission error:', error);
        return of({
          success: false,
          message: 'webPos.Closings.MoneyClosingErrorMessage',
          errors: [error.message || 'webPos.Errors.UnknownError']
        });
      })
    );
  }

  /**
   * Submit valuables closing transaction to API
   */
  submitValuablesClosing(data: ValuablesClosingData): Observable<ClosingTransactionResponse> {

    const request = this.buildClosingRequest(
      data, 
      data.TenderType,
      data.TenderSubType,
      TransactionTypeCodes.PaperValuablesClosing, 
      FinancialTransactionType.MoveFromTillToSafe, 
      TenderMovementReasonCode.PAPER_VALUABLES_CLOSING, 
      'webPos.Closings.ValuablesClosingLabel'
    );
    return from(this.posDataService.createFinancialTransaction(request)).pipe(
      map((response: CreateFinancialTransactionResponse | null) => {
        const success = !!response;
        let trxUID: string | undefined;
        
        if (response && response.records && response.records.length > 0 && response.records[0].components) {
           const headerComponent = response.records[0].components.find((component): component is { trxUID: string } => {
             return typeof component === "object" && component !== null && "trxUID" in component;
           });
           if (headerComponent) {
             trxUID = headerComponent.trxUID;
           }
        }

        return {
            success,
            message: success ? 'webPos.Closings.ValuablesClosingSubmittedMessage' : 'webPos.Closings.ValuablesClosingFailedMessage',
            transactionId: success ? 'generated-id' : undefined,
            trxUID: trxUID
        };
      }),
      catchError((error) => {
        console.error('Valuables closing submission error:', error);
        return of({
          success: false,
          message: 'webPos.Closings.ValuablesClosingErrorMessage',
          errors: [error.message || 'webPos.Errors.UnknownError']
        });
      })
    );
  }

  /**
   * Build CreateFinancialTransactionRequest for closing transactions
   */
  private buildClosingRequest(
    data: MoneyClosingData | ValuablesClosingData, 
    tenderType: TenderType,
    tenderSubType: string,
    transactionTypeCode: TransactionTypeCodes,
    financialTransactionType: FinancialTransactionType,
    reasonCode: string,
    reasonTextKey: string
  ): CreateFinancialTransactionRequest {
    const request = new CreateFinancialTransactionRequest();
    request.TransactionTypeCode = transactionTypeCode;
    request.FinancialTransactionType = financialTransactionType;
    request.TotalPrice = data.TotalValue;
    request.CurrencyISOCode = data.CurrencyCode;
    request.TenderType = tenderType;
    request.TenderSubType = tenderSubType;
    request.ReasonCode = reasonCode;
    // Translate the reason text synchronously if it's a key, otherwise use as-is
    request.ReasonText = this.translateService.instant(reasonTextKey);
    
    // Build note based on data type
    if (tenderType === TenderType.Cash) {
      const moneyData = data as MoneyClosingData;
      request.Note = data.Note;
    } else {
      const valuablesData = data as ValuablesClosingData;
      request.Note = `Total: ${valuablesData.TotalValue}, ${this.translateService.instant('webPos.Closings.Registered')}: ${valuablesData.TotalRegisteredValue}, ${this.translateService.instant('webPos.Closings.Difference')}: ${valuablesData.Difference}`;
    }
    
    // Store detailed data in AdditionalJsonData as JSON object (not stringified)
    // Normalize DenominationDetails to ensure Count is a number, not an array
    const normalizedData = this.normalizeDenominationData(data);
    request.AdditionalJsonData = JSON.stringify(normalizedData);
    
    request.DenominationItems = this.convertToDenominationItems(
      data.DenominationDetails,
      data.CurrencyCode
    );
    
    // Build attributes based on data type
    const baseAttributes = {
      'Difference': data.Difference.toString(),
      'Surplus': data.Surplus.toString(),
      'Shortage': data.Shortage.toString(),
      'DenominationCount': data.DenominationDetails.length.toString(),
      'PrintReceipt': data.PrintReceipt.toString()
    };

    if (tenderType === TenderType.Cash) {
      const moneyData = data as MoneyClosingData;
      request.Attributes = {
        ...baseAttributes,
        'TotalCash': moneyData.TotalValue.toString(),
        'AccountingState': moneyData.TotalRegisteredValue.toString()
      };
    } else {
      const valuablesData = data as ValuablesClosingData;
      request.Attributes = {
        ...baseAttributes,
        'ValuableType': valuablesData.ValuableType,
        'TotalValue': valuablesData.TotalValue.toString(),
        'TotalRegisteredValue': valuablesData.TotalRegisteredValue.toString()
      };
    }

    return request;
  }

  /**
   * Normalize DenominationDetails to ensure Count is always a number
   */
  private normalizeDenominationData(
    data: MoneyClosingData | ValuablesClosingData
  ): MoneyClosingData | ValuablesClosingData {
    return {
      ...data,
      DenominationDetails: data.DenominationDetails.map(detail => ({
        ...detail,
        Count: detail.Count,
        RegisteredCount: detail.RegisteredCount ?? 0
      }))
    };
  }

  /**
   * Convert DenominationDetail array to DenominationItem array
   */
  private convertToDenominationItems(
    details: DenominationDetail[],
    currencyISOCode: string = 'CZK'
  ): DenominationItem[] {
    return details.map(detail => ({
      NominalValue: detail.DenominationValue,
      Caption: detail.DenominationValue.toString(),
      CurrencyISOCode: currencyISOCode,
      Value: detail.DenominationValue,
      Quantity: detail.Count,
      Total: detail.Total,
      OptimalQuantity: detail.RegisteredCount ?? 0,
      DifferenceQuantity: detail.Count - (detail.RegisteredCount ?? 0)
    } as DenominationItem));
  }
}
